import { useMutation as useReactQueryMutation } from "@tanstack/react-query";
import { useAction } from "convex/react";
import { AlertCircleIcon, LoaderCircleIcon } from "lucide-react";
import { useQueryState } from "nuqs";
import { useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import { api } from "../../../convex/_generated/api";

function useBasePath() {
	const { pathname } = useLocation();
	return pathname.startsWith("/dev/workspace")
		? "/dev/workspace"
		: "/workspace";
}

export default function OutlookConnectionCallbackPage() {
	const [code] = useQueryState("code");
	const [state] = useQueryState("state");
	const [providerError] = useQueryState("error");
	const [errorDescription] = useQueryState("error_description");
	const navigate = useNavigate();
	const basePath = useBasePath();
	const completeOAuth = useAction(
		api.integrations.providers.microsoft.oauth.completeMsOutlookOAuth,
	);
	const [message, setMessage] = useState(
		"Finalizing your Outlook connection...",
	);
	const startedRef = useRef(false);

	const { mutate, status } = useReactQueryMutation({
		mutationKey: ["outlook-oauth", state, code],
		mutationFn: async () => {
			if (!code || !state)
				throw new Error("Missing Microsoft OAuth code or state.");
			await completeOAuth({ code, state });
		},
		onSuccess: () => {
			navigate(`${basePath}/connections`, {
				replace: true,
				state: { outlookConnectionStatus: "connected" },
			});
		},
		onError: (error: unknown) => {
			setMessage(
				error instanceof Error
					? error.message
					: "Unable to complete the Outlook connection.",
			);
		},
	});

	if (!startedRef.current && !providerError && code && state) {
		startedRef.current = true;
		mutate();
	}

	const isError = status === "error" || !!providerError;
	const errorMessage = providerError
		? `Microsoft returned an error: ${errorDescription || providerError}`
		: !code || !state
			? "Missing Microsoft OAuth code or state."
			: message;

	return (
		<div className="mx-auto flex min-h-[60vh] max-w-xl items-center px-6 py-12">
			<Card className="w-full">
				<CardHeader>
					<CardTitle>Outlook connection</CardTitle>
					<CardDescription>
						Clerical is finishing the Microsoft Outlook authorization flow.
					</CardDescription>
				</CardHeader>
				<CardContent className="space-y-4">
					{!isError ? (
						<div className="flex items-center gap-3 rounded-lg border bg-muted/40 px-4 py-3 text-sm text-muted-foreground">
							<LoaderCircleIcon className="size-4 animate-spin" />
							<span>{message}</span>
						</div>
					) : (
						<>
							<Alert variant="destructive">
								<AlertCircleIcon className="size-4" />
								<AlertTitle>Connection failed</AlertTitle>
								<AlertDescription>{errorMessage}</AlertDescription>
							</Alert>
							<Button
								variant="outline"
								onClick={() =>
									navigate(`${basePath}/connections`, { replace: true })
								}
							>
								Back to connections
							</Button>
						</>
					)}
				</CardContent>
			</Card>
		</div>
	);
}
